import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { APIContext } from '../../../Contexts/APIContext';
import HelpLink from '../../../Popups/HelpLink';
import WarningPopup from '../../../Popups/WarningPopup';
import yesNoDropdown from '../../../Utilities/Constants/YesNoDropdown';
import DataContainer from '../../../Utilities/DataContainer';
import RenderSelectField from '../../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../../Utilities/Forms/RenderTextField';
import DropDownViewModel from '../../../Utilities/ViewModels/DropDownViewModel';
import OriginAndDestinationTableViewModel from '../../ViewModels/OriginAndDestinationTableViewModel';
import ServiceVehiclesViewModel from '../../ViewModels/ServiceVehicles';
import SurveyDaysAdditionalDetailsViewModel from '../../ViewModels/SurveyDaysAdditionalDetailsViewModel';
import OriginAndDestinationTable from './OriginAndDestinationTable';
import ServiceVehiclesTable from './ServiceVehiclesTable';
import SurveyDates from './SurveyDates';
import SurveyTable from './SurveyTable';
import TransportTypes from './TransportTypes';
import { Button } from '@mui/material';
import ModalSplitChart from './ModalSplitChart';
import OccupancyChart from './OccupancyChart';
import ValidatedCheckBoxElement from "../../../Utilities/Forms/ValidatedCheckBoxElement";
import VehiclePercentageChart from './VehiclePercentageChart';
import OGVSplitChart from './OGVSplitChart';

interface IProps {
    id: string;
    surveyType: number;
    onSurveySelectionChange?: (mode: number | null, date: string) => void;
}

export default function SurvayDaysContainer(props: IProps) {
    const [modeOfTransport, setModeOfTransport] = useState<number | null>(null);
    const [date, setDate] = useState('');
    const { get, version } = useContext(APIContext);
    const [originAndDestinationSurvey, setOriginAndDestinationSurvey] = useState<OriginAndDestinationTableViewModel>(new OriginAndDestinationTableViewModel());
    const [serviceVehiclesSurvey, setServiceVehiclesSurvey] = useState<ServiceVehiclesViewModel>(new ServiceVehiclesViewModel());
    const [readOnly, setReadOnly] = useState(true);
    const [openModalChart, setOpenModalChart] = useState(false);
    const [openOGVChart, setOpenOGVChart] = useState(false);
    const [vehicleChartOpen, setVehicleChartOpen] = useState(false);
    const [openOccChart, setOpenOccChart] = useState(false);
    const [parkingPercentage, setParkingPercentage] = useState<number[]>([]);
    const [showAllOgvColumns, setShowAllOgvColumns] = useState(false);

    const formContext = useForm<SurveyDaysAdditionalDetailsViewModel>({
        defaultValues: new SurveyDaysAdditionalDetailsViewModel(),
    });
    const { reset, getValues, watch } = formContext;

    const comment = watch("comment");
    const alldetails = watch();
    const initialOccupancy = watch("initialOccupancy");
    const finalOccupancy = watch("finalOccupancy");
    const showTable = watch("servicingVehiclesRecorded")
    const servicingVehiclesRecorded = watch("servicingVehiclesRecorded") === 'Y';
    const [weatherDropdown, setWeatherDropdown] = useState<DropDownViewModel[]>([]);

    useEffect(() => {
        get('SurveyDays', 'GetWeatherDropdown', setWeatherDropdown);
    }, []);

    useEffect(() => {
        if (date != null && date != '') {
            get('SurveyDays', `GetParkingPercentage?id=${props.id}&date=${date}`, setParkingPercentage);
            get('SurveyDays', `GetSurveyDaysAdditionalDetails?id=${props.id}&date=${date}`, reset);
        }
    }, [date]);

    useEffect(() => {
        if (date?.length === 0 || modeOfTransport === null || props.id == null) {
            return;
        }

        if (modeOfTransport === 30) {
            get('SurveyDays', `GetOriginAndDestination?id=${props.id}`, setOriginAndDestinationSurvey);
            return;
        }

        if (modeOfTransport === 29) {
            setServiceVehiclesSurvey(new ServiceVehiclesViewModel());
            get('SurveyDays', `GetServiceVehicles?id=${props.id}&date=${date}`, (res) => {
                setServiceVehiclesSurvey(res ?? new ServiceVehiclesViewModel());
            });
            return;
        }
    }, [date, modeOfTransport, props.id, version]);

    useEffect(() => {
        setModeOfTransport(null);
        setDate('');
        setOriginAndDestinationSurvey(new OriginAndDestinationTableViewModel());
    }, [props.id]);

    useEffect(() => {
        if (props.onSurveySelectionChange) {
            props.onSurveySelectionChange(modeOfTransport, date);
        }
    }, [modeOfTransport, date]);

    const showModalSplitChart = () => setOpenModalChart(true);
    const closeModalSplitChart = () => setOpenModalChart(false);
    const showOGVSplitChart = () => setOpenOGVChart(true);
    const closeOGVSplitChart = () => setOpenOGVChart(false);
    const showVehicleChart = () => setVehicleChartOpen(true);
    const closeVehicleChart = () => setVehicleChartOpen(false);
    const showOccChart = () => setOpenOccChart(true);
    const closeOccChart = () => setOpenOccChart(false);

    const vehicleRows = [
        { key: 'ogv1', label: 'OGV (1)' },
        { key: 'ogv2', label: 'OGV (2)' },
        { key: 'lightGoods', label: 'Light Goods' },
        { key: 'motorCar', label: 'Motor Car' },
        { key: 'motorCycle', label: 'Motor Cycle' }
    ];

    return (
        <Grid container size={12}>
            <Grid container size={2}>
                {modeOfTransport == -1 && <Button variant="outlined" onClick={showModalSplitChart}>Show modal split chart</Button>}
                {modeOfTransport != -1 && <Button variant="outlined" onClick={showVehicleChart}>Show Vehicle Percentages</Button>}
                {modeOfTransport == 1 && <Button variant="outlined" onClick={showOGVSplitChart}>Show OGV Split chart</Button>}
                {modeOfTransport == 1 && !readOnly &&
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setShowAllOgvColumns(value => !value)}
                    >
                        {showAllOgvColumns ? 'Hide OGV split columns' : 'Show OGV split columns'}
                    </Button>
                }
                {modeOfTransport == 4 && <Button variant="outlined" onClick={showOccChart}>Show Vehicle Occupancy chart</Button>}
                <SurveyDates id={props.id} selectedDateIndex={date} setSelectedDateIndex={setDate} readOnly={readOnly} surveyType={props.surveyType} />
                <TransportTypes id={props.id} selectedType={modeOfTransport ?? 0} setSelectedType={setModeOfTransport} surveyType={props.surveyType} readOnly={readOnly} date={date} />
                <HelpLink label="Definitions" page="Count type definitions" />
            </Grid>
            <Grid container size={10}>
                {modeOfTransport !== null && modeOfTransport === 30 ? (
                    <OriginAndDestinationTable survey={originAndDestinationSurvey} />
                ) : modeOfTransport === 29 ? (
                    <ServiceVehiclesTable survey={serviceVehiclesSurvey} landUse={props.id.split('-')[1]} setValue={setServiceVehiclesSurvey} readOnly={readOnly} setReadOnly={setReadOnly} comment={comment ?? ''} additionalDetails={alldetails} />
                ) : (
                    <SurveyTable transportType={modeOfTransport} id={props.id} date={date} readOnly={readOnly} setReadOnly={setReadOnly} comment={comment ?? ''} additionalDetails={alldetails} showAllOgvColumns={showAllOgvColumns} setShowAllOgvColumns={setShowAllOgvColumns} />
                )}
            </Grid>
            <FormContainer formContext={formContext}>
                <Grid container size={12}>
                    <Grid size={3}>
                        <DataContainer title={'Weather'}>
                            <Stack spacing={1}>
                                <RenderSelectField label={'AM'} name={'weatherAm'} readOnly={readOnly} options={weatherDropdown} labelSize={2} inputSize={10} />
                                <RenderSelectField label={'PM'} name={'weatherPm'} readOnly={readOnly} options={weatherDropdown} labelSize={2} inputSize={10} />
                            </Stack>
                        </DataContainer>
                    </Grid>
                    <Grid size={3}>
                        <DataContainer title={'Car park occupancy'}>
                            <Stack spacing={1}>
                                <RenderTextField name={'initialOccupancy'} label={"Initial"} readOnly={readOnly} labelSize={2} inputSize={10} alwaysShow={true} />
                                <RenderTextField name={'finalOccupancy'} label={"Final"} readOnly={readOnly} labelSize={2} inputSize={10} alwaysShow={true} />
                                {(parkingPercentage.length > 0 && initialOccupancy != null && finalOccupancy != null) && (
                                    <Typography>
                                        {`Parking Capacity ${parkingPercentage[0].toFixed(2)}% (${parkingPercentage[1]} of ${parkingPercentage[2]} on-site spaces used)`}
                                    </Typography>
                                )}
                            </Stack>
                        </DataContainer>
                    </Grid>
                    <Grid size={6}>
                        <DataContainer title={'Comments'}>
                            <RenderTextField name={'comment'} readOnly={readOnly} maxRows={4} labelSize={12} inputSize={12} multiline={true} />
                        </DataContainer>
                    </Grid>
                    <Grid size={6}>
                        <DataContainer title={'Vehicle percentages'}>
                            <Stack spacing={1}>
                                <Stack spacing={1} direction={"row"}>
                                    <RenderTextField name={'ogv1Percentage'} label={"OGV (1)"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                    <RenderTextField name={'ogv2Percentage'} label={"OGV (2)"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                </Stack>
                                <Stack spacing={1} direction={"row"}>
                                    <RenderTextField name={'motorCarPercentage'} label={"Motor Car"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                    <RenderTextField name={'motorCyclePercentage'} label={"Motor Cycle"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                </Stack>
                                <Stack spacing={1} direction={"row"}>
                                    <RenderTextField name={'publicServicePercentage'} label={"Public Service"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                    <RenderTextField name={'lightGoodsPercentage'} label={"Light Goods"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                </Stack>
                                <Stack spacing={1} direction={"row"}>
                                    <RenderTextField name={'taxisPercentage'} label={"Taxis"} readOnly={readOnly} labelSize={4} inputSize={8} />
                                    <HelpLink page="Definitions of data fields for survey day details" label="Definitions" />
                                </Stack>
                                {!readOnly && <ValidatedCheckBoxElement name={'multiModalOveride'} label={"Multi-modal overide"} />}
                            </Stack>
                        </DataContainer>
                    </Grid>
                    <Grid size={6}>
                        <DataContainer title="Servicing/Standard Vehicle percentages">
                            <Stack spacing={1} direction={"row"}>
                                <RenderSelectField name={'servicingVehiclesRecorded'} label={"Servicing Vehicles Recorded"} readOnly={readOnly} options={yesNoDropdown} labelSize={8} inputSize={4} alwaysShow={true} />
                                <WarningPopup
                                    type="warning"
                                    title="Note on the recording of Servicing Vehicles"
                                    dialogSize="md"
                                    content="Please note that from 2018 onwards a Servicing Vehicles count has been included in all multi-modal TRICS surveys wherever possible. However, in some cases, for certain sites, it is not possible to record a Servicing Vehicles count. This tick box marker indicates whether or not a Servicing Vehicles count was part of the TRICS survey specification. If the box is ticked and there is no visible Servicing Vehicles count (i.e. there is not a Servicing Vehicles radio button option amongst the options on the left hand side of the survey count screen), this means that a count was undertaken but that no Servicing Vehicles visited the site through the duration of the survey. If the box is ticked and a Servicing Vehicles radio button is displayed as an option, then there will be such vehicles recorded. If the box is unticked, this means that a Servicing Vehicles count was not part of the TRICS survey specification for this site, and therefore the level of such vehicles are unknown. It should also be noted that for all multi-modal surveys, Servicing Vehicles will be included in the Total Vehicles, cars, OGV's, LGV's and motorcycle standard counts, which means that a visible Servicing Vehicles count does not show vehicles in addition to the Total Vehicles count. Also note that when calculating trip rates for Servicing Vehicles the same rules apply as per other modes, in that only survey days where a Servicing Vehicles count was included in the survey specification will be included in the data set used for calculation. Users should also be aware that if a servicing vehicle services more than one unit within a development, it is still only recorded once as it arrives at the development and once as it leaves."
                                />
                            </Stack>
                            {showTable &&
                                <Table size="small" sx={servicingVehiclesRecorded ? {} : { opacity: 0.5, pointerEvents: "none" }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>Vehicles</TableCell>
                                            <TableCell>Servicing %</TableCell>
                                            <TableCell>Standard %</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {vehicleRows.map((v) => (
                                            <TableRow key={v.key}>
                                                <TableCell><Typography variant="body2">{v.label}</Typography></TableCell>
                                                <TableCell>{readOnly ? <Typography>{getValues(v.key + 'Vehicle' as keyof SurveyDaysAdditionalDetailsViewModel)}</Typography> : <RenderTextField name={v.key + 'Vehicle' as keyof SurveyDaysAdditionalDetailsViewModel} readOnly={readOnly} />}</TableCell>
                                                <TableCell>{readOnly ? <Typography>{getValues(v.key + 'Servicing' as keyof SurveyDaysAdditionalDetailsViewModel)}</Typography> : <RenderTextField name={v.key + 'Servicing' as keyof SurveyDaysAdditionalDetailsViewModel} readOnly={readOnly} />}</TableCell>
                                                <TableCell>{readOnly ? <Typography>{getValues(v.key + 'Standard' as keyof SurveyDaysAdditionalDetailsViewModel)}</Typography> : <RenderTextField name={v.key + 'Standard' as keyof SurveyDaysAdditionalDetailsViewModel} readOnly={readOnly} />}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            }
                        </DataContainer>
                    </Grid>
                </Grid>
            </FormContainer>
            {openModalChart && <ModalSplitChart onClose={closeModalSplitChart} open={openModalChart} surveyId={props.id} date={date} />}
            {vehicleChartOpen && <VehiclePercentageChart onClose={closeVehicleChart} open={vehicleChartOpen} surveyId={props.id} date={date} />}
            {openOccChart && <OccupancyChart onClose={closeOccChart} open={openOccChart} surveyId={props.id} date={date} />}
            {openOGVChart && <OGVSplitChart onClose={closeOGVSplitChart} open={openOGVChart} surveyId={props.id} date={date} />}
        </Grid>
    );
}
